import apiClient from "./client";

// Types
export interface Campaign {
  id: number;
  uuid: string;
  name: string;
  type: "form" | "access" | "hidden";
  enabled: boolean;
  startAt: string | null;
  endAt: string | null;
  discountConfig: DiscountConfig | null;
  thankYouMessage: string | null;
  ctaUrl: string | null;
  ctaText: string | null;
  displayMode: "step" | "all";
  isActive: boolean;
  canBeEnabled: boolean;
  codesCount?: number;
  questionsCount?: number;
  questions?: CampaignQuestion[];
  createdAt: string;
  updatedAt: string;
}

export interface DiscountConfig {
  type: "percentage" | "fixed" | "plan_unlock";
  plans?: {
    standard?: { discount: number; unit: "percent" | "ron" };
    premium?: { discount: number; unit: "percent" | "ron" };
  };
  features?: string[];
}

export interface CampaignQuestion {
  id: number;
  type: "text" | "single_choice" | "multiple_choice";
  text: string;
  options: string[] | null;
  required: boolean;
  order: number;
}

export interface VoucherCode {
  id: number;
  value: string;
  singleUse: boolean;
  maxUses: number | null;
  usedCount: number;
  validFrom: string | null;
  validUntil: string | null;
  isValid: boolean;
  remainingUses: number | null;
  createdAt: string;
}

export interface CampaignStats {
  totalCodes: number;
  usedCodes: number;
  totalRedemptions: number;
  assignedVouchers: number;
  usedVouchers: number;
  conversionRate: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
  };
}

export interface CreateCampaignRequest {
  name: string;
  type: "form" | "access" | "hidden";
  startAt?: string | null;
  endAt?: string | null;
  discountConfig?: DiscountConfig | null;
  thankYouMessage?: string | null;
  ctaUrl?: string | null;
  ctaText?: string | null;
  displayMode?: "step" | "all";
}

export interface UpdateCampaignRequest {
  name?: string;
  type?: "form" | "access" | "hidden";
  enabled?: boolean;
  startAt?: string | null;
  endAt?: string | null;
  discountConfig?: DiscountConfig | null;
  thankYouMessage?: string | null;
  ctaUrl?: string | null;
  ctaText?: string | null;
  displayMode?: "step" | "all";
}

export interface GenerateCodesRequest {
  count: number;
  prefix?: string;
  length?: number;
  singleUse?: boolean;
  maxUses?: number;
  validFrom?: string | null;
  validUntil?: string | null;
}

export interface CreateQuestionRequest {
  type: "text" | "single_choice" | "multiple_choice";
  text: string;
  options?: string[];
  required?: boolean;
}

// API Functions

/**
 * List all campaigns with optional filtering
 */
export async function getCampaigns(options?: {
  type?: "form" | "access" | "hidden";
  status?: "enabled" | "disabled" | "active";
  sort?: string;
  direction?: "asc" | "desc";
  page?: number;
  perPage?: number;
}): Promise<PaginatedResponse<Campaign>> {
  const params: Record<string, string | number> = {};
  if (options?.type) params.type = options.type;
  if (options?.status) params.status = options.status;
  if (options?.sort) params.sort = options.sort;
  if (options?.direction) params.direction = options.direction;
  if (options?.page) params.page = options.page;
  if (options?.perPage) params.per_page = options.perPage;

  const { data } = await apiClient.get<PaginatedResponse<Campaign>>(
    "management/campaigns",
    { params }
  );
  return data;
}

/**
 * Get a single campaign by UUID
 */
export async function getCampaign(uuid: string): Promise<Campaign> {
  const { data } = await apiClient.get<Campaign>(
    `management/campaigns/${uuid}`
  );
  return data;
}

/**
 * Create a new campaign
 */
export async function createCampaign(
  request: CreateCampaignRequest
): Promise<Campaign> {
  const { data } = await apiClient.post<Campaign>(
    "management/campaigns",
    {
      name: request.name,
      type: request.type,
      start_at: request.startAt,
      end_at: request.endAt,
      discount_config: request.discountConfig,
      thank_you_message: request.thankYouMessage,
      cta_url: request.ctaUrl,
      cta_text: request.ctaText,
      display_mode: request.displayMode,
    }
  );
  return data;
}

/**
 * Update a campaign
 */
export async function updateCampaign(
  uuid: string,
  request: UpdateCampaignRequest
): Promise<Campaign> {
  const { data } = await apiClient.patch<Campaign>(
    `management/campaigns/${uuid}`,
    {
      name: request.name,
      type: request.type,
      enabled: request.enabled,
      start_at: request.startAt,
      end_at: request.endAt,
      discount_config: request.discountConfig,
      thank_you_message: request.thankYouMessage,
      cta_url: request.ctaUrl,
      cta_text: request.ctaText,
      display_mode: request.displayMode,
    }
  );
  return data;
}

/**
 * Delete a campaign
 */
export async function deleteCampaign(uuid: string): Promise<void> {
  await apiClient.delete(`management/campaigns/${uuid}`);
}

/**
 * Get campaign statistics
 */
export async function getCampaignStats(uuid: string): Promise<CampaignStats> {
  const { data } = await apiClient.get<CampaignStats>(
    `management/campaigns/${uuid}/stats`
  );
  return data;
}

// Voucher Codes

/**
 * List voucher codes for a campaign
 */
export async function getCampaignCodes(
  uuid: string,
  options?: {
    status?: "valid" | "expired" | "exhausted";
    page?: number;
    perPage?: number;
  }
): Promise<PaginatedResponse<VoucherCode>> {
  const params: Record<string, string | number> = {};
  if (options?.status) params.status = options.status;
  if (options?.page) params.page = options.page;
  if (options?.perPage) params.per_page = options.perPage;

  const { data } = await apiClient.get<PaginatedResponse<VoucherCode>>(
    `management/campaigns/${uuid}/codes`,
    { params }
  );
  return data;
}

/**
 * Generate voucher codes for a campaign
 */
export async function generateCampaignCodes(
  uuid: string,
  request: GenerateCodesRequest
): Promise<{ generated: number; codes: { id: number; value: string }[] }> {
  const { data } = await apiClient.post(
    `management/campaigns/${uuid}/codes`,
    {
      count: request.count,
      prefix: request.prefix,
      length: request.length,
      single_use: request.singleUse,
      max_uses: request.maxUses,
      valid_from: request.validFrom,
      valid_until: request.validUntil,
    }
  );
  return data;
}

/**
 * Update a voucher code
 */
export async function updateCampaignCode(
  uuid: string,
  codeId: number,
  request: {
    validFrom?: string | null;
    validUntil?: string | null;
    maxUses?: number;
  }
): Promise<VoucherCode> {
  const { data } = await apiClient.patch<VoucherCode>(
    `management/campaigns/${uuid}/codes/${codeId}`,
    {
      valid_from: request.validFrom,
      valid_until: request.validUntil,
      max_uses: request.maxUses,
    }
  );
  return data;
}

/**
 * Delete a voucher code
 */
export async function deleteCampaignCode(
  uuid: string,
  codeId: number
): Promise<void> {
  await apiClient.delete(`management/campaigns/${uuid}/codes/${codeId}`);
}

// Questions

/**
 * Add a question to a campaign
 */
export async function addCampaignQuestion(
  uuid: string,
  request: CreateQuestionRequest
): Promise<CampaignQuestion> {
  const { data } = await apiClient.post<CampaignQuestion>(
    `management/campaigns/${uuid}/questions`,
    request
  );
  return data;
}

/**
 * Update a question
 */
export async function updateCampaignQuestion(
  uuid: string,
  questionId: number,
  request: Partial<CreateQuestionRequest>
): Promise<CampaignQuestion> {
  const { data } = await apiClient.patch<CampaignQuestion>(
    `management/campaigns/${uuid}/questions/${questionId}`,
    request
  );
  return data;
}

/**
 * Delete a question
 */
export async function deleteCampaignQuestion(
  uuid: string,
  questionId: number
): Promise<void> {
  await apiClient.delete(
    `management/campaigns/${uuid}/questions/${questionId}`
  );
}

/**
 * Reorder questions
 */
export async function reorderCampaignQuestions(
  uuid: string,
  questionIds: number[]
): Promise<{ questions: CampaignQuestion[] }> {
  const { data } = await apiClient.post(
    `management/campaigns/${uuid}/questions/reorder`,
    { order: questionIds }
  );
  return data;
}
